import { useEffect, useRef, memo } from 'react';

const ParticleBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    // Particle properties
    const particlesArray: Particle[] = [];
    const numberOfParticles = Math.min(50, window.innerWidth / 20); // Responsive number of particles

    // Particle class
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      alpha: number;
      growing: boolean;

      constructor() {
        // Canvas is guaranteed to be non-null here due to our above check
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.color = 'rgba(37, 99, 235, 0.1)'; // Light blue color with opacity
        this.alpha = Math.random() * 0.2 + 0.05;
        this.growing = Math.random() > 0.5;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Canvas is guaranteed to be non-null here due to our above check
        if (this.x > canvas!.width) this.x = 0;
        else if (this.x < 0) this.x = canvas!.width;

        if (this.y > canvas!.height) this.y = 0;
        else if (this.y < 0) this.y = canvas!.height;
        
        // Pulsating effect
        if (this.growing) {
          this.size += 0.01;
          if (this.size > 4) this.growing = false;
        } else {
          this.size -= 0.01;
          if (this.size < 1) this.growing = true;
        }
      }

      draw() {
        if (!ctx) return;
        ctx.fillStyle = `rgba(37, 99, 235, ${this.alpha})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Create particles
    for (let i = 0; i < numberOfParticles; i++) {
      particlesArray.push(new Particle());
    }

    // Animation function
    const animate = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();
        
        // Connect particles with lines
        for (let j = i; j < particlesArray.length; j++) {
          const dx = particlesArray[i].x - particlesArray[j].x;
          const dy = particlesArray[i].y - particlesArray[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(37, 99, 235, ${0.1 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particlesArray[i].x, particlesArray[i].y);
            ctx.lineTo(particlesArray[j].x, particlesArray[j].y);
            ctx.stroke();
          }
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    // Function to add bubble elements
    const addBubbleElements = () => {
      // Add bubble elements to the DOM
      const container = document.createElement('div');
      container.className = 'absolute inset-0 overflow-hidden -z-10 pointer-events-none particle-background-bubbles';
      document.body.appendChild(container);
      
      // Create 6 random bubble elements
      for (let i = 0; i < 6; i++) {
        const bubble = document.createElement('div');
        const size = Math.random() * 15 + 5; // Size between 5-20%
        const left = Math.random() * 90; // Position between 0-90%
        const top = Math.random() * 90;
        const delay = Math.random() * 5; // Animation delay between 0-5s
        const duration = Math.random() * 5 + 5; // Animation duration between 5-10s
        
        bubble.className = 'absolute rounded-full blur-[60px] animate-pulse-slow';
        bubble.style.width = `${size}%`;
        bubble.style.height = `${size}%`;
        bubble.style.left = `${left}%`;
        bubble.style.top = `${top}%`;
        bubble.style.animationDelay = `${delay}s`;
        bubble.style.animationDuration = `${duration}s`;
        
        // Alternating colors
        if (i % 3 === 0) {
          bubble.style.background = 'rgba(37, 99, 235, 0.05)'; // Primary light
        } else if (i % 3 === 1) {
          bubble.style.background = 'rgba(16, 185, 129, 0.05)'; // Secondary light
        } else {
          bubble.style.background = 'rgba(124, 58, 237, 0.05)'; // Tertiary color
        }
        
        container.appendChild(bubble);
      }
    };
    
    // Add the bubble elements
    addBubbleElements();

    // Start animation
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      // Cancel animation frame
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Remove bubble elements on cleanup
      const bubbleContainer = document.querySelector('.particle-background-bubbles');
      if (bubbleContainer) {
        document.body.removeChild(bubbleContainer);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full -z-10 opacity-40 dark:opacity-20"
    />
  );
};

// Use memo to prevent re-rendering when parent components change
export default memo(ParticleBackground);
export { ParticleBackground }; 